// Agent MLS IDs: the agent MLS ID read (GET /agent-mls-ids.json) joined by id with the Featured rows and boards
// (GET /client-boards.json) and the agents (GET /agents.json), for the getAgentMlsIds selector. The Featured Listings
// page, the MLS ID dialogs and the agent profile read their states from it. The service owns "is this the same MLS ID";
// nothing here compares ID strings.
//
// The model: each AgentBoard row (an entry) that needs a slot is paired with one Featured row (a slot) of the same value
// on the board, by count. Within a value, the holders in row id order take its slots lowest id first. A value with as
// many slots as holders is aligned. A holder left over is a stray; a slot left over is an orphan. When several agents
// hold a value that has fewer slots than holders, nobody is paired: a person picks which agent keeps it (Shared).
import { AgentMlsIdResource } from "api/resource/agent-mls-id";
import { ClientBoardListResource, ClientBoardResource } from "api/resource/client-board";
import { AgentMlsIds, ClientBoardLink, MlsIdAgent } from "model/agent-mls-ids";

// null, "" and "None" all mean no value.
export const isNone = (value: string | null | undefined) => {
	const trimmed = (value ?? "").trim().toLowerCase();
	return trimmed === "" || trimmed === "none";
};

export const alternateIdOf = (row: ClientBoardResource) => (isNone(row.officeId) ? null : row.officeId);

export const entriesOf = (agentMlsIds: AgentMlsIds, agentId: number, boardId: number) =>
	agentMlsIds.entries.filter((entry) => entry.agentId === agentId && entry.boardId === boardId);

// The agent MLS ID read is for office-type accounts only, so any other account builds from the client boards alone
// (read null, agentAccount false). The boards are the ones with a client-board row, "None" rows included, by board id.
export const buildAgentMlsIds = (read: AgentMlsIdResource[] | null, clientBoards: ClientBoardListResource, agentList: MlsIdAgent[], agentAccount = true): AgentMlsIds => {
	const nameOf = (boardId: number) => clientBoards.clientBoards.find((row) => row.boardId === boardId)?.boardName ?? "";
	const rows = new Map(clientBoards.clientBoards.filter((row) => !isNone(row.agentId)).map((row) => [row.id, row]));
	const boards = Array.from(new Set(clientBoards.clientBoards.map((row) => row.boardId))).sort((a, b) => a - b)
		.map((id) => ({ id, pending: clientBoards.clientBoards.some((row) => row.boardId === id && row.status === "PENDING") }));
	const agents = read ? agentList : [];
	const entries = (read ?? []).slice().sort((a, b) => a.id - b.id);

	// Pair each value's holders with its slots, by count.
	const slotOf = new Map<number, number>();
	const holderOf = new Map<number, number>();
	const sharedEntries = new Set<number>();
	const sharedRows = new Set<number>();
	const values = new Map<number, AgentMlsIdResource[]>();
	entries.forEach((entry) => {
		const key = entry.valueRowIds.length ? entry.valueRowIds[0] : entry.id;
		values.set(key, [...(values.get(key) ?? []), entry]);
	});
	values.forEach((group) => {
		const holders = group.filter((entry) => entry.needsSlot);
		// The read's own slots: for a moment after a write the client boards can lag behind, and a slot the client boards
		// don't have yet is skipped where it's shown rather than unpaired.
		const slots = group[0].clientBoardIds.slice().sort((a, b) => a - b);
		if (new Set(holders.map((entry) => entry.agentId)).size > 1 && holders.length > slots.length && slots.length > 0) {
			holders.forEach((entry) => sharedEntries.add(entry.id));
			slots.forEach((rowId) => sharedRows.add(rowId));
			return;
		}
		holders.slice(0, slots.length).forEach((entry, i) => {
			slotOf.set(entry.id, slots[i]);
			holderOf.set(slots[i], entry.id);
		});
	});

	// An agent saved without a name shows as "Agent <id>" rather than a blank cell.
	const agentById = new Map(agents.map((agent) => [agent.id, agent.name?.trim() ? agent : { ...agent, name: `Agent ${agent.id}` }]));

	// Each AgentBoard row's link, from the pairing above.
	const alternateIdOfRow = (rowId: number) => {
		const row = rows.get(rowId);
		return row ? alternateIdOf(row) : null;
	};
	const clientBoardLinks = new Map<number, ClientBoardLink>(entries.map((entry): [number, ClientBoardLink] => {
		const agentBoardId = entry.id;
		if (slotOf.has(agentBoardId)) {
			const clientBoardId = slotOf.get(agentBoardId)!;
			return [agentBoardId, { agentBoardId, clientBoardId, state: "linked", alternateId: alternateIdOfRow(clientBoardId) }];
		}
		if (sharedEntries.has(agentBoardId)) {
			const clientBoardId = entry.clientBoardIds.find((rowId) => sharedRows.has(rowId)) ?? null;
			return [agentBoardId, { agentBoardId, clientBoardId, state: "shared", alternateId: null }];
		}
		if (entry.needsSlot) {
			return [agentBoardId, { agentBoardId, clientBoardId: null, state: "stray", alternateId: null }];
		}
		const off = entry.optOut && agentById.get(entry.agentId)?.billable === true && !clientBoards.officeAccount;
		return [agentBoardId, { agentBoardId, clientBoardId: null, state: off ? "off" : "plain", alternateId: off ? entry.alternateId : null }];
	}));

	return {
		officeAccount: clientBoards.officeAccount,
		agentAccount,
		boards: boards.map((board) => ({ boardId: board.id, name: nameOf(board.id), pending: board.pending })),
		agents: agentById,
		entries,
		rows,
		slotOf,
		holderOf,
		sharedEntries,
		sharedRows,
		clientBoardLinks,
	};
};
